import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useTheme } from '../../contexts/ThemeContext';

const Sidebar = ({ 
  userRole = 'admin', 
  isCollapsed = false, 
  onToggleCollapse,
  className = '' 
}) => {
  const { setDarkMode } = useTheme();

  // Set dark mode for admin users via ThemeContext (not direct DOM manipulation)
  useEffect(() => {
    if (userRole === 'admin') {
      setDarkMode();
    }
  }, [userRole]);

  const [expandedSections, setExpandedSections] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const getSidebarItems = () => {
    switch (userRole) {
      case 'admin':
        return [
          {
            name: 'Dashboard',
            icon: 'LayoutDashboard',
            path: '/admin-dashboard',
            badge: null
          },
          {
            name: 'User Management',
            icon: 'Users',
            children: [
              { name: 'All Users', path: '/admin-dashboard?section=all-users', icon: 'Users' },
              { name: 'Admins', path: '/admin-dashboard?section=admins', icon: 'Shield' },
              { name: 'Editors', path: '/admin-dashboard?section=editors', icon: 'Edit' },
              { name: 'Artists', path: '/admin-dashboard?section=artists', icon: 'Palette' }
            ]
          },
          {
            name: 'System Analytics',
            icon: 'BarChart3',
            path: '/admin-dashboard?section=analytics',
            badge: 'New'
          },
          {
            name: 'Content Oversight',
            icon: 'FileText',
            children: [
              { name: 'Pending Reviews', path: '/admin-dashboard?section=pending-reviews', icon: 'Clock', badge: '12' },
              { name: 'Published Content', path: '/admin-dashboard?section=published-content', icon: 'CheckCircle' },
              { name: 'Flagged Content', path: '/admin-dashboard?section=flagged-content', icon: 'AlertTriangle', badge: '3' }
            ]
          },
          {
            name: 'System Settings',
            icon: 'Settings',
            children: [
              { name: 'General', path: '/admin-dashboard?section=settings-general', icon: 'Settings' },
              { name: 'Security', path: '/admin-dashboard?section=settings-security', icon: 'Lock' },
              { name: 'Integrations', path: '/admin-dashboard?section=settings-integrations', icon: 'Zap' }
            ]
          }
        ];
      case 'editor':
        return [
          {
            name: 'Dashboard',
            icon: 'LayoutDashboard',
            path: '/editor-dashboard',
            badge: null
          },
          {
            name: 'Artist Management',
            icon: 'Users',
            children: [
              { name: 'All Artists', path: '/artist-content-management', icon: 'Users' },
              { name: 'Pending Approval', path: '/artist-detail-editor-view', icon: 'Clock', badge: '5' },
              { name: 'Active Artists', path: '/artist-content-management', icon: 'CheckCircle' }
            ]
          },
          {
            name: 'Content Review',
            icon: 'FileText',
            children: [
              { name: 'Review Queue', path: '/artist-detail-editor-view', icon: 'Eye', badge: '8' },
              { name: 'Published Content', path: '/artist-detail-editor-view', icon: 'CheckCircle' },
              { name: 'Draft Content', path: '/artist-detail-editor-view', icon: 'Edit3' }
            ]
          },
          {
            name: 'Mobile Preview',
            icon: 'Smartphone',
            path: '/mobile-app-preview',
            badge: null
          },
          {
            name: 'Analytics',
            icon: 'BarChart3',
            path: '/editor-dashboard',
            badge: null
          }
        ];
      case 'artist':
        return [
          {
            name: 'Content Manager',
            icon: 'FileText',
            path: '/artist-content-management',
            badge: null
          },
          {
            name: 'Profile Sections',
            icon: 'User',
            children: [
              { name: 'Basic Info', path: '/artist-content-management', icon: 'User' },
              { name: 'Biography', path: '/artist-content-management', icon: 'FileText' },
              { name: 'Contact', path: '/artist-content-management', icon: 'Mail' },
              { name: 'Social Links', path: '/artist-content-management', icon: 'Link' }
            ]
          },
          {
            name: 'Portfolio',
            icon: 'Image',
            children: [
              { name: 'Gallery', path: '/artist-content-management', icon: 'Image' },
              { name: 'Featured Works', path: '/artist-content-management', icon: 'Star' },
              { name: 'Collections', path: '/artist-content-management', icon: 'Folder' }
            ]
          },
          {
            name: 'Mobile Preview',
            icon: 'Smartphone',
            path: '/mobile-app-preview',
            badge: null
          }
        ];
      default:
        return [];
    }
  };

  const sidebarItems = getSidebarItems();

  const handleNavigation = (path) => {
    navigate(path);
  };

  const toggleSection = (sectionName) => {
    if (isCollapsed) return;
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev?.[sectionName]
    }));
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const isActiveSection = (item) => {
    if (item?.path) return isActivePath(item?.path);
    if (item?.children) {
      return item?.children?.some(child => isActivePath(child?.path));
    }
    return false;
  };

  return (
    <aside className={`fixed left-0 top-16 bottom-0 z-40 bg-surface border-r border-border elevation-1 transition-medium ${
      isCollapsed ? 'w-16' : 'w-64'
    } ${className}`}>
      <div className="flex flex-col h-full">
        {/* Collapse Toggle */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          {!isCollapsed && (
            <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
              Navigation
            </h2>
          )}
          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className="transition-smooth z-50 hover:bg-accent/20 hover:text-accent-foreground"
            >
              <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
            </Button>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {sidebarItems?.map((item, index) => (
            <div key={`nav-item-${index}`} className="relative">
              {/* Main Item */}
              <div
                role="button"
                tabIndex={0}
                className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-smooth select-none z-10 relative ${
                  isActiveSection(item)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-text-primary hover:bg-accent/20 hover:text-accent-foreground'
                }`}
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  console.log('Menu item clicked:', item?.name);
                  
                  if (item?.path) {
                    handleNavigation(item?.path);
                  } else if (item?.children) {
                    toggleSection(item?.name);
                  }
                }}
                onKeyDown={(e) => {
                  if (e?.key === 'Enter' || e?.key === ' ') {
                    e?.preventDefault();
                    if (item?.path) {
                      handleNavigation(item?.path);
                    } else if (item?.children) {
                      toggleSection(item?.name);
                    }
                  }
                }}
              >
                <div className="flex items-center space-x-3 pointer-events-none">
                  <Icon 
                    name={item?.icon} 
                    size={18} 
                    className={isActiveSection(item) ? 'text-primary-foreground' : 'group-hover:text-accent-foreground'}
                  />
                  {!isCollapsed && (
                    <span className="text-sm font-medium">{item?.name}</span>
                  )}
                </div>
                
                {!isCollapsed && (
                  <div className="flex items-center space-x-2 pointer-events-none">
                    {item?.badge && (
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item?.badge === 'New' ?'bg-success text-success-foreground' :'bg-warning text-warning-foreground'
                      }`}>
                        {item?.badge}
                      </span>
                    )}
                    {item?.children && (
                      <Icon 
                        name="ChevronDown" 
                        size={14} 
                        className={`transition-transform ${
                          expandedSections?.[item?.name] ? 'rotate-180' : ''
                        } ${isActiveSection(item) ? 'text-primary-foreground' : 'group-hover:text-accent-foreground'}`}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Children Items */}
              {item?.children && !isCollapsed && expandedSections?.[item?.name] && (
                <div className="ml-6 mt-1 space-y-1 animate-fade-in">
                  {item?.children?.map((child, childIndex) => (
                    <div
                      key={`child-item-${index}-${childIndex}`}
                      role="button"
                      tabIndex={0}
                      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-smooth select-none z-10 relative ${
                        isActivePath(child?.path)
                          ? 'bg-accent text-accent-foreground'
                          : 'text-text-secondary hover:bg-accent/20 hover:text-accent-foreground'
                      }`}
                      onClick={(e) => {
                        e?.preventDefault();
                        e?.stopPropagation();
                        console.log('Child menu item clicked:', child?.name);
                        handleNavigation(child?.path);
                      }}
                      onKeyDown={(e) => {
                        if (e?.key === 'Enter' || e?.key === ' ') {
                          e?.preventDefault();
                          handleNavigation(child?.path);
                        }
                      }}
                    >
                      <div className="flex items-center space-x-3 pointer-events-none">
                        <Icon 
                          name={child?.icon} 
                          size={16} 
                          className={isActivePath(child?.path) ? 'text-accent-foreground' : ''}
                        />
                        <span className="text-sm">{child?.name}</span>
                      </div>
                      {child?.badge && (
                        <span className="px-2 py-1 text-xs bg-warning text-warning-foreground rounded-full pointer-events-none">
                          {child?.badge}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          {!isCollapsed ? (
            <div className="text-center">
              <p className="text-xs text-text-secondary">IDArtists Admin</p>
              <p className="text-xs text-text-secondary">v2.1.0</p>
            </div>
          ) : (
            <div className="flex justify-center">
              <Icon name="Info" size={16} className="text-text-secondary" />
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;